import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Table, Button, Tag, Space, Typography, Divider, Skeleton } from "antd";
import { PrinterOutlined, ClockCircleOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import Layout from "../../ReusableComponents/Layout";
import Letter from "../Letter";
import getSingleGuest from "../../Redux/APIs/GuestAPIs/getSingleGuest";
import IntructionLetter from "../IntructionLetter";
import { Icons } from "../../Utils/icons-utils";
import { format, parseISO } from "date-fns";
import AdressCard from "./components/AdressCard";
import useLetterPrinterMutation from "../../api/mutations/useGuestsLetterPrinterMutation";
import useLetterStatusQuery from "../../api/queries/useGuestsLetterStatusQuery";
import useLetterSettingsQuery from "../../api/queries/useLetterSettingsQuery";
import LetterStatusIcon from "./components/LetterStatusIcon";
import { formatAddress } from "./utils";

const { Text, Title } = Typography;

const GuestDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const location = useLocation();
  const params = useParams();
  const { data: letterStatus, refetch, isLoading } = useLetterStatusQuery(data?._id);
  const { data: letterSettings } = useLetterSettingsQuery();

  useEffect(() => {
    if (params?.id) {
      dispatch(getSingleGuest(params.id, "", (guest) => setData(guest)));
    }
  }, [params?.id]);

  const { mutate: handlePrintClick } = useLetterPrinterMutation(() => refetch());

  const summaryColumns = [
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Orgs", key: "orgs", align: "center", render: () => data?.companies?.length },
    {
      title: "Created",
      key: "createdAt",
      align: "center",
      render: () => (data?.createdAt ? format(parseISO(data.createdAt), "dd MMM yy") : "-"),
    },
    {
      title: "Move Date",
      key: "moveDate",
      align: "center",
      render: () => (data?.movingDate ? format(parseISO(data.movingDate), "dd MMM yy") : "-"),
    },
    {
      title: "Old Printed",
      key: "oldPrinted",
      align: "center",
      render: () => <LetterStatusIcon status={letterStatus?.isLetterPrinted} isLoading={isLoading} />,
    },
    {
      title: "New Printed",
      key: "newPrinted",
      align: "center",
      render: () => <LetterStatusIcon status={letterStatus?.isNewLetterPrinted} isLoading={isLoading} />,
    },
  ];

  return (
    <Layout>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16, padding: 0, fontSize: 14 }}
      >
        Back
      </Button>
      <Table
        columns={summaryColumns}
        dataSource={data?._id ? [data] : []}
        rowKey="_id"
        pagination={false}
        bordered
        size="small"
        style={{ marginBottom: 24 }}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          {isLoading ? (
            <Card><Skeleton active /></Card>
          ) : (
            <AdressCard
              address={data?.oldAddress}
              isOldAddress
              onPrintClick={async () =>
                await handlePrintClick({ guestId: data._id, isLetterPrinted: true })
              }
              printStatus={letterStatus?.isLetterPrinted}
              unPrintClick={async () =>
                await handlePrintClick({ guestId: data._id, isLetterPrinted: false })
              }
              email={data?.email}
              isGuestDetails
            />
          )}
        </Col>
        <Col xs={24} md={12}>
          {isLoading ? (
            <Card><Skeleton active /></Card>
          ) : (
            <AdressCard
              address={data?.newAddress}
              isOldAddress={false}
              onPrintClick={async () =>
                await handlePrintClick({ guestId: data._id, isNewLetterPrinted: true })
              }
              printStatus={letterStatus?.isNewLetterPrinted}
              unPrintClick={async () =>
                await handlePrintClick({ guestId: data._id, isNewLetterPrinted: false })
              }
              email={data?.email}
              isGuestDetails
            />
          )}
        </Col>
      </Row>

      {data?.companies?.map((company, index) => (
        <Card key={company._id || index} size="small" style={{ marginBottom: 12 }}>
          <Row align="middle" gutter={16}>
            <Col>
              <img
                src={Icons[company?.service?.[0]?.name]}
                alt={company?.service?.[0]?.name}
                style={{ width: 40, height: 40, objectFit: "contain" }}
              />
            </Col>
            <Col flex="auto">
              <Text strong>{company?.name}</Text>
              <br />
              {company?.printed ? (
                <Tag color="green">Printed</Tag>
              ) : (
                <Tag color="orange">To be printed</Tag>
              )}
            </Col>
            {company.completed ? (
              <Col>
                <Space>
                  <Letter
                    button={
                      <button style={{ border: "none", background: "transparent", cursor: "pointer" }}>
                        <PrinterOutlined style={{ fontSize: 20, color: "#00AFEE" }} />
                      </button>
                    }
                    id={company?._id}
                    name={`${data?.firstName} ${data?.lastName}`}
                    oldAddress={formatAddress(data?.oldAddress)}
                    newAddress={formatAddress(data?.newAddress)}
                    companyName={company?.name}
                    companyAddress={company?.address}
                    movingDate={data?.movingDate}
                    dob={data?.dateOfBirth}
                    email={data?.email}
                    phone={data?.phone}
                    serviceName={company?.service?.[0]?.name}
                    required={company?.required}
                    requiredDetails={company?.required}
                    requiredAPI={company?.required}
                    printSettings={letterSettings}
                  />
                  <IntructionLetter
                    id={company?._id}
                    firstName={data?.firstName}
                    lastName={data?.lastName}
                    name={`${data?.firstName} ${data?.lastName}`}
                    oldAddress={formatAddress(data?.oldAddress)}
                    newAddress={formatAddress(data?.newAddress)}
                    companyName={company?.name}
                    companyAddress={company?.address}
                    movingDate={data?.movingDate}
                    dob={data?.dateOfBirth}
                    email={data?.email}
                    phone={data?.phone}
                    serviceName={company?.service?.[0]?.name}
                    required={company?.required}
                    requiredDetails={company?.required}
                    requiredAPI={company?.required}
                  />
                </Space>
              </Col>
            ) : (
              <Col>
                <Tag icon={<ClockCircleOutlined />} color="processing">In Progress</Tag>
              </Col>
            )}
          </Row>
          {company.completed && company?.required && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <Space wrap>
                {Object.entries(company.required).map(([key, value]) => (
                  <div key={key}>
                    <Text type="secondary" style={{ fontSize: 12 }}>{key}</Text>
                    <br />
                    <Text>{value}</Text>
                  </div>
                ))}
              </Space>
            </>
          )}
        </Card>
      ))}
    </Layout>
  );
};

export default GuestDetails;
